import { createElement } from 'react';
import type { CmsMeta } from './cmsMeta';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CalendlyWidget } from '../../components/App/CalendlyWidget/CalendlyWidget';
import { PriceTagSimple } from '../../components/PriceTagSimple/PriceTagSimple';
import { BrandHeader } from '../BrandHeader/BrandHeader';
import { CheckmarkList } from '../CheckmarkList/CheckmarkList';
import { CmsMessage } from '../CmsMessage/CmsMessage';
import { ContentCard } from '../ContentCard/ContentCard';
import { EmbeddedYoutubeVideo } from '../EmbeddedYoutubeVideo/EmbeddedYoutubeVideo';
import { ErrorBoundary } from '../ErrorBoundary';
import { CampaignProducts } from '../product/CampaignProducts';
import { TorkFacebookTracking } from '../TorkFaceboookTracking/TorkFacebookTracking';
import { TorkTracking } from '../TorkTracking/TorkTracking';
import { CmsActivity } from './CmsActivity/CmsActivity';
import { CmsAtomBackground } from './CmsAtomBackground/CmsAtomBackground';
import { CmsBadge } from './CmsBadge/CmsBadge';
import { CmsBanner } from './CmsBanner/CmsBanner';
import { CmsBreadcrumbs } from './CmsBreadcrumbs/CmsBreadcrumbs';
import { CmsButton } from './CmsButton/CmsButton';
import { CmsCard } from './CmsCard/CmsCard';
import { CmsCustomerServiceEmail } from './CmsCustomerServiceEmail';
import { CmsCustomerServicePhone } from './CmsCustomerServicePhone';
import { CmsFaq } from './CmsFaq/CmsFaq';
import { CmsFaqHeading } from './CmsFaqHeading/CmsFaqHeading';
import { CmsFaqQuestion } from './CmsFaqQuestion/CmsFaqQuestion';
import { CmsGreeting } from './CmsGreeting/CmsGreeting';
import { CmsGrid } from './CmsGrid/CmsGrid';
import CmsHeader from './CmsHeader/CmsHeader';
import { CmsHtml } from './CmsHtml/CmsHtml';
import { CmsImage } from './CmsImage/CmsImage';
import { CmsImageContentCard } from './CmsImageContentCard/CmsImageContentCard';
import { CmsImageHeader } from './CmsImageHeader/CmsImageHeader';
import { CmsMolecule } from './CmsMolecule/CmsMolecule';
import { CmsMostBought } from './CmsMostBought/CmsMostBought';
import { CmsReviewsCarousel } from './CmsOfficeServices/CmsReviewsCarousel/CmsReviewsCarousel';
import { CmsServicesCarousel } from './CmsOfficeServices/CmsServicesCarousel/CmsServicesCarousel';
import { CmsWorkyAppCard } from './CmsOfficeServices/CmsWorkyAppCard/CmsWorkyAppCard';
import { CmsWorkyInfo } from './CmsOfficeServices/CmsWorkyInfo';
import { CmsWorkyInfoColumn } from './CmsOfficeServices/CmsWorkyInfoColumn';
import { CmsPopularCategories } from './CmsPopularCategories/CmsPopularCategories';
import { CmsPopularCategoriesGrid } from './CmsPopularCategoriesGrid/CmsPopularCategoriesGrid';
import { CmsPopularCategoryItem } from './CmsPopularCategoryItem/CmsPopularCategoryItem';
import { CmsProductImageGrid } from './CmsProductImageGrid/CmsProductImageGrid';
import { CmsProductList } from './CmsProductList/CmsProductList';
import { CmsProductListCategoryLoop54 } from './CmsProductListCategoryLoop54/CmsProductListCategoryLoop54';
import { CmsProductListLoop54 } from './CmsProductListLoop54/CmsProductListLoop54';
import { CmsProductListSimple } from './CmsProductListSimple/CmsProductListSimple';
import { CmsProductListSimpleLoop54 } from './CmsProductListSimpleLoop54/CmsProductListSimpleLoop54';
import { CmsQuote } from './CmsQuote/CmsQuote';
import { CmsSpacer } from './CmsSpacer/CmsSpacer';
import { CmsStack } from './CmsStack/CmsStack';
import { CmsStory } from './CmsStory/CmsStory';
import { CmsPanel, CmsSplitPanel } from './CmsSplitPanel/CmsSplitPanel';
import { CmsKpi } from './CmsKpi/CmsKpi';
import { CmsOndioSpecialBanner, CmsSuperBanner, CmsSuperBannerCarousel } from './CmsSuperBanner/CmsSuperBanner';
import { CmsTopBanner } from './CmsTopBanner/CmsTopBanner';
import { CmsTopSellingProducts } from './CmsTopSellingProducts/CmsTopSellingProducts';
import { GridBannerPlacement } from './GridBannerPlacement/GridBannerPlacement';
import { InfoBox } from './InfoBox/InfoBox';
import { ProductBoxBanner } from './ProductBoxBanner/ProductBoxBanner';

export const cmsComponents: Record<string, any> = {
	CmsProductList,
	CmsProductListSimple,
	CmsProductImageGrid,
	CmsBanner,
	CmsMolecule,
	CmsAtomBackground,
	InfoBox,
	CmsImage,
	CmsButton,
	CmsGrid,
	CmsGreeting,
	CmsCustomerServiceEmail,
	CmsCustomerServicePhone,
	CmsFaq,
	CmsFaqHeading,
	CmsFaqQuestion,
	ContentCard,
	CmsMostBought,
	CmsImageContentCard,
	CheckmarkList,
	Helmet,
	Link,
	PriceTagSimple,
	CmsPopularCategories,
	CmsPopularCategoriesGrid,
	CmsPopularCategoryItem,
	CmsSuperBanner,
	CmsSuperBannerCarousel,
	CmsWorkyInfo,
	CmsWorkyInfoColumn,
	CmsWorkyAppCard,
	CmsCard,
	CalendlyWidget,
	CmsServicesCarousel,
	CmsReviewsCarousel,
	CmsImageHeader,
	EmbeddedYoutubeVideo,
	TorkTracking,
	TorkFacebookTracking,
	CmsHtml,
	CmsMessage,
	CampaignProducts,
	CmsProductListSimpleLoop54,
	CmsProductListLoop54,
	CmsProductListCategoryLoop54,
	CmsStack,
	CmsBadge,
	CmsSpacer,
	BrandHeader,
	ProductBoxBanner,
	GridBannerPlacement,
	CmsTopSellingProducts,
	CmsStory,
	CmsSplitPanel,
	CmsPanel,
	CmsKpi,
	CmsQuote,
	CmsActivity,
	CmsHeader,
	CmsTopBanner,
	CmsBreadcrumbs,
	CmsOndioSpecialBanner,
};

/**
 * A node as the cms stores it: tag, props, then children.
 *
 * A child can be a string, because most nodes end in text - `['p', {}, 'Hej']`. The renderer
 * has always allowed it (`Array.isArray(e) ? asJsx(e) : e`); the type did not, which only
 * surfaced once examples in cmsMeta started being type-checked.
 */
export type JsxArgs = [string, Record<string, any>, ...(JsxArgs | string)[]];

type CmsComponentProps = { structure: JsxArgs | undefined };

/**
 * A document emptied in the cms is published as `[]`, which is not nothing: it is a node whose name
 * is `undefined`, and react throws "Element type is invalid" over it. The ErrorBoundary below only
 * catches that in the browser - `renderToString` has no boundaries - so on the server the exception
 * escapes the render and the whole page becomes a 500. Five published categories stood empty that
 * way. Nothing to render is therefore answered here, before react is asked to render it.
 */
const isRenderable = (structure: JsxArgs | undefined): structure is JsxArgs =>
	Array.isArray(structure) && typeof structure[0] === 'string' && structure[0] !== '';

export const CmsComponent = ({ structure }: CmsComponentProps) => {
	if (!isRenderable(structure)) return null;
	return (
		<ErrorBoundary>
			<RenderedJsx structure={structure} />
		</ErrorBoundary>
	);
};

const RenderedJsx = ({ structure }: { structure: JsxArgs }) => {
	function asJsx(node: JsxArgs): JSX.Element | null {
		if (!isRenderable(node)) return null;

		const [name, props, ...children] = node;
		const type = cmsComponents[name] ?? name;
		return createElement(type, props, ...children.map((e) => (Array.isArray(e) ? asJsx(e) : e)));
	}

	return asJsx(structure);
};

/**
 * Components that come from a package, so there is no file of ours to keep their meta beside.
 * The registry is where they are named, so the registry documents them.
 */
export const cmsMetas: Record<string, CmsMeta> = {
	Helmet: {
		description:
			"What search engines and browser tabs see. Every new page should carry one, with a title of its own and a meta description - a page without a Helmet silently inherits the site default, which is the front page's title, so it is indexed under the wrong name. Write the title for the search result rather than for the page: the subject first, the market last. The description is a sentence or two, around 150 characters, saying what the page answers.",
		example: [
			'Helmet',
			{ index: 0 },
			['title', { index: 0 }, 'Alles, was Sie für ein gutes Homeoffice brauchen - ondio.de'],
			[
				'meta',
				{
					index: 1,
					name: 'description',
					content:
						'Richten Sie Ihr Homeoffice mit Schreibtisch, Bürostuhl und Zubehör ein. Ergonomisch arbeiten, auch zu Hause.',
				},
			],
		],
	},
	Link: {
		description:
			'A link to somewhere else on the site. Use it rather than an `a` for internal paths - it navigates without reloading the page. Keep `a` for external addresses, mailto and tel.',
		example: ['Link', { index: 0, to: '/', className: 'button' }, 'Jetzt einkaufen'],
	},
};
